import { XYCoord } from 'react-dnd';

import { DragId, DragItem, DropItem } from './interactive-drag-drop.constants';

type ItemList = {
  zones: {
    dragItemId: DragId | null;
    previewItemId: DragId | null;
  }[];
};

export function findNextFreePosition<T extends ItemList>(
  droppedAt: number,
  dropItems: T[]
): number {
  // not dropped find next free slot
  if (droppedAt === -1) {
    for (let i = 0; i < dropItems.length; i++) {
      const zone = dropItems[i].zones.find(z => z.dragItemId === null);
      if (zone) {
        return i;
      }
    }
  }

  if (dropItems[droppedAt + 1]) {
    if (dropItems[droppedAt + 1].zones.some(z => z.dragItemId === null)) {
      return droppedAt + 1;
    } else {
      return findNextFreePosition(droppedAt + 1, dropItems);
    }
  }

  return findNextFreePosition(-1, dropItems);
}

export function findPreviousFreePosition<T extends ItemList>(
  droppedAt: number,
  dropItems: T[]
): number {
  // not dropped find next free slot
  if (droppedAt === -1) {
    for (let i = dropItems.length - 1; i >= 0; i--) {
      const zone = dropItems[i].zones.find(z => z.dragItemId === null);
      if (zone) {
        return i;
      }
    }
  }

  if (dropItems[droppedAt - 1]) {
    if (dropItems[droppedAt - 1].zones.some(z => z.dragItemId === null)) {
      return droppedAt - 1;
    } else {
      return findPreviousFreePosition(droppedAt - 1, dropItems);
    }
  }

  return findPreviousFreePosition(-1, dropItems);
}

export function moveToFreePosition(
  id: DragId,
  items: DropItem[],
  direction: 'right' | 'left'
): {
  dropItems: DropItem[];
  movedTo: number;
} {
  const droppedAt = findDroppedAtIndex(items, id);
  const moveToIndex =
    direction === 'right'
      ? findNextFreePosition(droppedAt, items)
      : findPreviousFreePosition(droppedAt, items);

  removeItemFromDropZone(items, id);

  const zone = items[moveToIndex].zones.find(z => z.dragItemId === null);
  zone!.previewItemId = id;
  return { dropItems: items, movedTo: moveToIndex };
}

export function markDragItemAsDropped<
  T extends { id: DragId; isDropped: boolean },
>(id: DragId, items: T[]): T[] {
  const item = items.find(item => item.id === id && item.isDropped === false);
  if (item) {
    item.isDropped = true;
  }
  return items;
}

export function isEveryDropAreaOccupied<T extends ItemList>(
  items: T[]
): boolean {
  for (let i = 0; i < items.length; i++) {
    if (!items[i].zones.every(item => item.dragItemId || item.previewItemId)) {
      return false;
    }
  }
  return true;
}

export function isEveryItemDropped(items: DropItem[]): boolean {
  if (items.length === 0) return false;

  for (let i = 0; i < items.length; i++) {
    const dropItem = items[i];
    if (!dropItem.zones.every(zone => zone.dragItemId)) {
      return false;
    }
  }
  return true;
}

export function isEveryDraggableDropped(items: DragItem[]): boolean {
  if (items.length === 0) return false;

  for (let i = 0; i < items.length; i++) {
    if (!items[i].isDropped) {
      return false;
    }
  }
  return true;
}

export function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export function addItemToDropZone(dropItem: DropItem, dragItem: DragItem) {
  const zone = dropItem.zones.find(z => z.dragItemId === null);
  if (zone) {
    zone.dragItemId = dragItem.id;
  }
  return dropItem;
}

export function removeItemFromDropZone(
  dropItems: DropItem[],
  sourceId: DragId
) {
  dropItems.forEach(item => {
    const zone = item.zones.find(
      z => z.dragItemId === sourceId || z.previewItemId === sourceId
    );
    if (zone) {
      zone.dragItemId = null;
      zone.previewItemId = null;
    }
  });
  return dropItems;
}

export function findDropItemByPreviewId(dropItems: DropItem[], id: DragId) {
  for (let i = 0; i < dropItems.length; i++) {
    const zone = dropItems[i].zones.find(z => z.previewItemId === id);
    if (zone) {
      return dropItems[i];
    }
  }
  return undefined;
}

function findDroppedAtIndex(dropItems: DropItem[], id: DragId) {
  for (let i = 0; i < dropItems.length; i++) {
    const zone = dropItems[i].zones.find(
      z => z.dragItemId === id || z.previewItemId === id
    );
    if (zone) {
      return i;
    }
  }

  return -1;
}

export function translateElement({
  element,
  currentOffset,
  initialOffset,
  scale,
  onComplete,
}: {
  element: HTMLElement;
  currentOffset: XYCoord;
  initialOffset: XYCoord;
  scale: number;
  onComplete?: () => void;
}) {
  const clonedElement: HTMLElement = element.cloneNode(true) as HTMLElement;
  document.body.appendChild(clonedElement);

  element.style.transition = 'none';
  element.style.opacity = '0';

  clonedElement.style.pointerEvents = 'none';
  clonedElement.style.position = 'fixed';
  clonedElement.style.transformOrigin = 'top left';
  clonedElement.style.transform = `scale(${scale}) translate(${
    currentOffset.x / scale
  }px, ${currentOffset.y / scale}px)`;
  clonedElement.style.left = '0';
  clonedElement.style.top = '0';
  clonedElement.style.opacity = '0';
  clonedElement.style.zIndex = '1000';
  clonedElement.style.width = `${124 / scale}px`;
  clonedElement.style.height = `${124 / scale}px`;
  clonedElement.style.transition = 'transform 300ms ease, opacity 400ms ease';
  const child = clonedElement.childNodes[0] as HTMLElement;
  if (child) {
    child.style.opacity = '1';
    child.style.width = '100%';
    child.style.height = '100%';
  }

  requestAnimationFrame(() => {
    clonedElement.style.opacity = '1';

    setTimeout(() => {
      clonedElement.style.transform = `scale(${scale}) translate(${
        initialOffset.x / scale
      }px, ${initialOffset.y / scale}px)`;
    }, 50);

    setTimeout(() => {
      requestAnimationFrame(() => {
        element.style.opacity = '1';
        element.style.transition = '';
        document.body.removeChild(clonedElement);
        onComplete?.();
      });
    }, 400);
  });
}
